<template>
    <div class="content-container">
        <div class="container">
            <div class="row">

                <!-- Title -->
                <div class="col-12">
                    <div class="title mt-5">
                        <h1>Drop-off location</h1>
                    </div>
                </div>

                <div class="col-12" v-if="loading">
                    <loading-state></loading-state>
                    <h4 class="text-center">Setting selected store as drop off location, please wait...</h4>
                </div>

                <!-- Map -->
                <div class="col-12" v-if="!loading">
                    <div class="card card-body">
                        <gmap-map
                            :center="latLng"
                            class="map-container"
                            ref="map"
                            :zoom="8">
                            <GmapMarker
                                :position="latLng"
                                :clickable="true"
                                :draggable="true"
                                :icon="{ url: ('https://s3.eu-west-2.amazonaws.com/wefixuk/images/icons/van-map-marker.png')}"/>
                        </gmap-map>
                    </div>
                </div>

                <!-- Details -->
                <div class="col-12" v-if="!loading">
                    <div class="card">
                        <div class="card-header">
                            <h4>{{ dropOffStore.address.town }}</h4>
                            <p class="mb-0">
                                <i class="fa fa-map-marker-alt text-muted text-sm"></i> {{ dropOffStore.distance }} Miles
                            </p>
                        </div>
                        <div class="card-body">
                            <drop-off-date-alert></drop-off-date-alert>
                            <div class="row">
                                <dl class="col-md-6" v-if="dropOffStore && dropOffStore.address">
                                    <dt>Address</dt>
                                    <dd>{{ dropOffStore.address.line_1 }}</dd>
                                    <dd>{{ dropOffStore.address.line_2 }}</dd>
                                    <dd>{{ dropOffStore.address.town }}</dd>
                                    <dd>{{ dropOffStore.address.postcode }}</dd>
                                    <dt>Phone</dt>
                                    <dd>{{ dropOffStore.phone_number }}</dd>
                                </dl>
                                <dl class="col-md-6" v-if="dropOffStore.opening_times">
                                    <dt>Opening hours</dt>
                                    <dd class="d-flex" v-for="(openingTime, key) in dropOffStore.opening_times" :key="key">
                                        <span class="day">{{ openingTime.day }}</span>
                                        <span class="ml-auto">{{ formatTime(openingTime.open) }} - {{ formatTime(openingTime.close) }}</span>
                                    </dd>
                                </dl>
                                <div class="col-12">
                                    <hr />
                                    <h4>We can come to you instead!</h4>
                                    <p>
                                        If you'd prefer, we can repair your device wherever suits you. Whether it's at your home, place of work or even the gym, we can get your device repaired and returned to you within an hour.
                                    </p>
                                    <p class="text-center">
                                        <button @click="switchToDoorStopRepair()" class="btn btn-outline-primary btn-lg">Book the doorstep service</button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Footer -->
            <footer class="footer" :class="footerClass">
                <div class="container">
                    <router-link :to="{ name: 'dropOffLocationSelect'}" class="btn btn-outline-secondary back-btn btn-lg"><i class="fa fa-chevron-left text-sm mr-3"></i> Back</router-link>
                    <button @click="nextPage" :disabled="loading" class="btn btn-primary btn-lg continue-btn icon-right ml-auto">Next <i class="fa fa-chevron-right ml-3 text-sm"></i></button>
                </div>
            </footer>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import DropOffDateAlert from "Components/drop-off/DropOffDateAlert";

    export default {
        name: "DropOffLocationDetails",
        components: {DropOffDateAlert},
        data() {
            return {
                loading: true,
                dropOffStore: null
            }
        },

        mounted() {
            this.checkCanBeOnPage();
            this.getDropOffStoreData().then((store) => {
                this.dropOffStore = store;
                this.loading = false;
            });
        },

        computed: {
            ...mapGetters([
                'requestedDropOffStoreId',
                'store',
                'storesWithDropOffEnabled',
                'availabilities',
                'bookingPostcode'
            ]),

            footerClass() {
                return (window.innerHeight < 768) ? "fixed-footer" : "";
            },

            latLng() {
                if(this.dropOffStore) {
                    return {
                        lat: parseFloat(this.dropOffStore.address.latitude),
                        lng: parseFloat(this.dropOffStore.address.longitude),
                    }
                }
                return false;
            },
        },

        methods: {
            ...mapActions([
                'loadAvailability',
                'setBookingPostcode',
                'displayToastFromResponseObjectOfErrors',
                'setCustomerPostcodeCache',
                'setAddress',
                'setDropOffCompanyStoreId',
                'displayToast',
                'setRepairLocationOption'
            ]),

            checkCanBeOnPage() {
                if(!this.requestedDropOffStoreId) {
                    return this.$router.push({name: 'dropOffLocationSelect'})
                }
            },

            getDropOffStoreData() {
                return new Promise((resolve) => {
                    if (this.store.id === this.requestedDropOffStoreId) return resolve(this.store); // A store has already been set. We don't need to do it again.

                    let store = this.storesWithDropOffEnabled.find((store) => {
                        return store.id === this.requestedDropOffStoreId
                    });
                    resolve(store);
                });
            },

            formatTime(time) {
                return time.substring(0, time.length - 3);
            },

            nextPage() {
                this.loading = true;

                let customersPostcode = this.bookingPostcode;

                this.setBookingPostcode(this.dropOffStore.address.postcode).then(() => {
                    this.setCustomerPostcodeCache(customersPostcode).then(() => {
                        this.setDropOffCompanyStoreId(this.dropOffStore.id).then(() => {
                            this.$router.push({name: 'repairDates'});
                        }).catch(errors => this.displayToastFromResponseObjectOfErrors(errors));
                    });
                }).catch(errors => {
                    if(errors.response.data.code === "BK03") {
                        this.displayToast({
                            text: 'We currently have no technician availability for this area, please choose another drop off store',
                            type: "error",
                            duration: 6000
                        });
                        return this.$router.push({name: 'dropOffLocationSelect'})
                    }
                    this.displayToastFromResponseObjectOfErrors(errors);
                });
            },

            switchToDoorStopRepair() {
                this.setRepairLocationOption('doorstep').then(() => {
                    this.$router.push({name: 'repairDates'});
                })
            }
        }
    }
</script>

<style scoped>
    .day {
        text-transform: capitalize;
    }
</style>